import { useState, useRef } from 'react'
import axios from 'src/utils/axios'
import { deleteVcfFile } from '../../../api/vcf'
import { deleteFastqFile, deleteFastqPair } from '../../../api/fastq'
import { deleteOntBamFile } from '../../../api/ont'
import { deletePacBioBamFile } from '../../../api/pacbio'
import { annotateFile, updateFinishInfo, updateFileNotes } from '../../../api/file'
import { downloadFastqPair } from '../../../api/fastq/download'
import { API_BASE_URL } from '../../../constants'
import { startGenericAnalysis } from '../../../api/analysis/annotate'
import { refusalTextOf } from 'src/services/analysisStart'
import { useOptimisticUpdates } from './useOptimisticUpdates'

export const useFileActions = (filesApi) => {
  const [downloadingFastqId, setDownloadingFastqId] = useState(null)
  const [downloadingBamId, setDownloadingBamId] = useState(null)
  const [downloadingReportId, setDownloadingReportId] = useState(null)
  const [downloadingVcfId, setDownloadingVcfId] = useState(null)
  const [annotationStatus, setAnnotationStatus] = useState({})

  const optimisticUpdates = useOptimisticUpdates()

  const handleDownloadReport = async (vcfId, sampleName) => {
    setDownloadingReportId(vcfId)
    // if sampleName includes .gz, remove the .gz
    sampleName = sampleName.replace('.gz', '')
    sampleName = sampleName.replace('.vcf', '')
    sampleName = sampleName.replace('.raw', '')
    // add .vcf.gz
    sampleName = sampleName + '_report.docx'

    try {
      const response = await axios.get(`${API_BASE_URL}/vcf/download-report/${vcfId}`, {
        responseType: 'blob',
      })

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]))
      // Create a temporary anchor element
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      // Set the file name
      a.download = `${sampleName}`
      // Append to the body and click
      document.body.appendChild(a)
      a.click()
      // Clean up
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading the file:', error)
      // Handle the error (e.g., show an error message to the user)
    } finally {
      setDownloadingReportId(null)
    }
  }

  const handleDownloadRawVcfOverBackend = async (vcfId, sampleName) => {
    // if sampleName includes .gz, remove the .gz
    sampleName = sampleName.replace('.gz', '')
    sampleName = sampleName.replace('.vcf', '')
    sampleName = sampleName.replace('.raw', '')
    // add .vcf.gz
    sampleName = sampleName + '.vcf.gz'

    const {
      data: { url },
    } = await axios.get(`${API_BASE_URL}/vcf/vcf-link/${vcfId}`)

    const restOfUrl = url.replace(/^https?:\/\/[^/]+\/api\/v1/, '')
    const newUrl = `${API_BASE_URL}${restOfUrl}`

    const link = document.createElement('a')
    link.href = newUrl
    link.download = `${sampleName}_vcf.zip`
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const handleDownloadRawVcfS3 = async (vcfId) => {
    const {
      data: { vcf_url, lifted_unmap_url },
    } = await axios.get(`${API_BASE_URL}/vcf/download-vcf-s3/${vcfId}/`)

    const triggerDownload = (fileUrl) =>
      new Promise((resolve) => {
        const link = document.createElement('a')
        link.href = fileUrl
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(resolve, 250)
      })

    if (vcf_url) {
      await triggerDownload(vcf_url)
    }
    if (lifted_unmap_url) {
      await triggerDownload(lifted_unmap_url)
    }
  }

  const handleDownloadRawVcf = async (vcfId, sampleName) => {
    setDownloadingVcfId(vcfId)
    const isS3DownloadUploadEnabled = process.env.REACT_APP_IS_S3_DOWNLOAD_UPLOAD_ENABLED === 'true'

    try {
      if (isS3DownloadUploadEnabled) {
        await handleDownloadRawVcfS3(vcfId)
      } else {
        await handleDownloadRawVcfOverBackend(vcfId, sampleName)
      }
    } catch (error) {
      console.error('Error downloading the file:', error)
    } finally {
      setDownloadingVcfId(null)
    }
  }

  const handleFileAnnotation = async (
    selectedFile,
    annotation,
    setErrorModalOpen,
    setNeededBalance,
    setPaymentSnackbarOpen,
    setNewBalance,
    setDeductedAmount,
  ) => {
    // Determine the type and ID from the selected file object
    const { id, type } = selectedFile?.fastq_pair_id
      ? { id: selectedFile.fastq_pair_id, type: 'FASTQ' }
      : selectedFile?.ont_bam_id
      ? { id: selectedFile.ont_bam_id, type: 'ONT_BAM' }
      : selectedFile?.pacbio_bam_id
      ? { id: selectedFile.pacbio_bam_id, type: 'PACBIO_BAM' }
      : { id: selectedFile.vcf_id, type: 'VCF' }

    if (!id) {
      console.error('Could not determine file ID for annotation.')
      return false
    }

    setAnnotationStatus((prev) => ({ ...prev, [id]: 'loading' }))

    try {
      // --- USE THE NEW GENERIC API FUNCTION ---
      const res = await startGenericAnalysis(id, type, annotation)
      // --- END OF CHANGE ---

      if (res?.error === 'Insufficient balance') {
        setErrorModalOpen(true)
        setNeededBalance(res?.needed_balance)
        setAnnotationStatus((prev) => ({ ...prev, [id]: 'idle' }))
        return res
      }

      // The refresh is now handled in the calling component (AnalysisView)
      // filesApi.refresh()

      setPaymentSnackbarOpen(true)
      setNewBalance(res?.new_balance)
      setDeductedAmount(res?.deducted_amount)
      setAnnotationStatus((prev) => ({ ...prev, [id]: 'success' }))

      await new Promise((resolve) => setTimeout(resolve, 2000))
      setAnnotationStatus((prev) => ({ ...prev, [id]: 'idle' }))
      return res
    } catch (error) {
      console.error('Annotation failed:', error)
      setAnnotationStatus((prev) => ({ ...prev, [id]: 'idle' }))
      // A clean refusal (nothing created or charged) carries the server's reason.
      const refused = refusalTextOf(error)
      return refused ? { error: refused, refused: true } : false
    }
  }

  const handleFileDelete = async (row) => {
    // Get the file ID for optimistic updates
    const fileId = row.fastq_pair_id || row.vcf_id || row.fastq_file_id || row.ont_bam_id || row.pacbio_bam_id

    // Optimistically remove the file from UI immediately for instant feedback
    if (optimisticUpdates && fileId) {
      optimisticUpdates.addDeletingFile(fileId)
    }

    try {
      // Immediate API call - no delays for the smoothest experience
      if (row.vcf_id && !row.fastq_pair_id) {
        // VCF file deletion
        await deleteVcfFile(row.vcf_id)
      } else if (row.fastq_pair_id) {
        // FASTQ pair deletion - use the pair ID directly
        await deleteFastqPair(row.fastq_pair_id)
      } else if (row.fastq_file_id) {
        // Single FASTQ file deletion (unpaired)
        await deleteFastqFile(row.fastq_file_id)
      } else if (row.ont_bam_id) {
        // ONT BAM file deletion
        await deleteOntBamFile(row.ont_bam_id)
      } else if (row.pacbio_bam_id) {
        // PacBio BAM file deletion
        await deletePacBioBamFile(row.pacbio_bam_id)
      }

      // File is already visually deleted, just clean up optimistic state
      if (optimisticUpdates && fileId) {
        setTimeout(() => {
          optimisticUpdates.removeDeletingFile(fileId)
        }, 500) // Small delay to let animation complete
      }

      // Background refresh for data consistency
      setTimeout(() => {
        filesApi.refresh()
      }, 100)
    } catch (error) {
      console.error('Error deleting file:', error)

      // Restore the file in UI if deletion failed
      if (optimisticUpdates && fileId) {
        setTimeout(() => {
          optimisticUpdates.removeDeletingFile(fileId)
        }, 100)
      }

      // Throw error to be handled by DeleteFileButton
      throw new Error('Failed to delete file. Please try again.')
    }
  }

  const setFinishedInfo = (row) => {
    const id = row.fastq_pair_id ? row.fastq_pair_id : row.vcf_id
    updateFinishInfo(id).then(() => {
      filesApi.refresh()
    })
  }

  const setFileNotes = async (row, notes) => {
    const id = row.fastq_pair_id ? row.fastq_pair_id : row.vcf_id
    try {
      await updateFileNotes(id, notes)
      await filesApi.refresh()
    } catch (error) {
      console.error('Failed to update notes:', error)
      throw error // Re-throw so NoteCard can handle the error
    }
  }

  const handleDownloadFastqS3 = async (fastqPairId, sampleName, onFailed, onSuccess) => {
    const {
      data: { pair_1_url, pair_2_url },
    } = await axios.get(`${API_BASE_URL}/fastq/download-pair-s3/${fastqPairId}/`)

    const triggerDownload = (fileUrl) =>
      new Promise((resolve) => {
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = fileUrl
        document.body.appendChild(iframe)

        // Give the browser a moment to start the download before triggering the next one
        setTimeout(() => {
          document.body.removeChild(iframe)
          resolve()
        }, 1000)
      })

    await triggerDownload(pair_1_url)
    await triggerDownload(pair_2_url)
  }

  const handleDownloadFastqOverBackend = async (fastqPairId, sampleName, onFailed, onSuccess) => {
    const {
      data: { url },
    } = await axios.get(`${API_BASE_URL}/fastq/pair-link/${fastqPairId}/`)

    const restOfUrl = url.replace(/^https?:\/\/[^/]+\/api\/v1/, '')
    const newUrl = `${API_BASE_URL}${restOfUrl}`

    const link = document.createElement('a')
    link.href = newUrl
    link.download = `${sampleName}_fastq_pair.zip`
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  const handleDownloadFastq = async (fastqPairId, sampleName, onFailed, onSuccess) => {
    setDownloadingFastqId(fastqPairId)

    const isS3DownloadUploadEnabled = process.env.REACT_APP_IS_S3_DOWNLOAD_UPLOAD_ENABLED === 'true'

    try {
      if (isS3DownloadUploadEnabled) {
        await handleDownloadFastqS3(fastqPairId, sampleName, onFailed, onSuccess)
      } else {
        await handleDownloadFastqOverBackend(fastqPairId, sampleName, onFailed, onSuccess)
      }
    } catch (error) {
      if (onFailed) onFailed()
    } finally {
      setDownloadingFastqId(null)
      if (onSuccess) onSuccess()
    }
  }

  const triggerDownload = (fileUrl) =>
    new Promise((resolve) => {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = fileUrl
      document.body.appendChild(iframe)
      setTimeout(() => {
        document.body.removeChild(iframe)
        resolve()
      }, 1000)
    })

  const handleDownloadBamS3 = async (bamId, bamType, sampleName, onFailed, onSuccess) => {
    const endpoint = bamType === 'ont' ? 'ont-bam/download-s3' : 'pacbio-bam/download-s3'
    const {
      data: { bam_url, bai_url },
    } = await axios.get(`${API_BASE_URL}/${endpoint}/${bamId}/`)

    if (!bam_url) throw new Error('BAM file not found')
    await triggerDownload(bam_url)
    if (bai_url) await triggerDownload(bai_url)
  }

  const handleDownloadBamOverBackend = async (bamId, bamType, sampleName, onFailed, onSuccess) => {
    const endpoint = bamType === 'ont' ? 'ont-bam/link' : 'pacbio-bam/link'
    const {
      data: { bam_url, bai_url },
    } = await axios.get(`${API_BASE_URL}/${endpoint}/${bamId}/`)

    const resolveUrl = (fileUrl) => {
      const restOfUrl = fileUrl.replace(/^https?:\/\/[^/]+\/api\/v1/, '')
      return `${API_BASE_URL}${restOfUrl}`
    }

    if (!bam_url) throw new Error('BAM file not found')
    await triggerDownload(resolveUrl(bam_url))
    if (bai_url) await triggerDownload(resolveUrl(bai_url))
  }

  const _handleDownloadBam = async (bamId, bamType, sampleName, onFailed, onSuccess) => {
    setDownloadingBamId(bamId)
    const isS3DownloadUploadEnabled = process.env.REACT_APP_IS_S3_DOWNLOAD_UPLOAD_ENABLED === 'true'

    try {
      if (isS3DownloadUploadEnabled) {
        await handleDownloadBamS3(bamId, bamType, sampleName, onFailed, onSuccess)
      } else {
        await handleDownloadBamOverBackend(bamId, bamType, sampleName, onFailed, onSuccess)
      }
    } catch (error) {
      console.error('Error downloading BAM:', error)
      if (onFailed) onFailed()
    } finally {
      setDownloadingBamId(null)
      if (onSuccess) onSuccess()
    }
  }

  const handleDownloadOntBam = (bamId, sampleName, onFailed, onSuccess) => {
    return _handleDownloadBam(bamId, 'ont', sampleName, onFailed, onSuccess)
  }
  const handleDownloadPacBioBam = (bamId, sampleName, onFailed, onSuccess) => {
    return _handleDownloadBam(bamId, 'pacbio', sampleName, onFailed, onSuccess)
  }

  return {
    downloadingFastqId,
    downloadingBamId,
    downloadingReportId,
    downloadingVcfId,
    annotationStatus,
    setAnnotationStatus,
    handleDownloadReport,
    handleDownloadRawVcf,
    handleFileAnnotation,
    handleFileDelete,
    setFinishedInfo,
    setFileNotes,
    handleDownloadFastq,
    handleDownloadOntBam,
    handleDownloadPacBioBam,
  }
}
